/**
 * Vacancy PDF Export, editorial document-style PDF in Vakature landing aesthetic.
 *
 * Design language follows the public landing page (frontend/src/landing-vakature.css):
 *   • paper-tinted background (#f6f4ef)
 *   • monospace eyebrow labels with letter-spacing (numbered: "01. OVER HET BEDRIJF")
 *   • bold deep-ink titles with generous tracking
 *   • teal accent (#0d9488, app brand)
 *   • subtle hairline section dividers
 *   • header band with Vakature/Humanaize branding
 *
 * jsPDF built-in fonts: helvetica (≈ IBM Plex Sans) + courier (≈ IBM Plex Mono).
 */
import { jsPDF } from 'jspdf';

// ── Brand palette, mirrors landing-vakature.css :root tokens ──
const C = {
  teal:    [13, 148, 136],   // --purple (was teal swap), primary brand
  ink:     [15, 17, 21],     // --ink, deep title text
  ink2:    [42, 45, 53],     // --ink-2, body text
  muted:   [90, 94, 104],    // --ink-3, muted/eyebrow secondary
  line:    [230, 228, 223],  // --line, hairline
  line2:   [210, 207, 200],  // --line-2, heavier hairline
  paper:   [246, 244, 239],  // --paper, page background
};

const SECTION_ORDER = [
  'job_title', 'about_company', 'job_description',
  'requirements', 'benefits', 'conditions',
];

const SECTION_LABELS = {
  job_title: 'Vacaturetitel',
  about_company: 'Over het bedrijf',
  job_description: 'Functieomschrijving',
  requirements: 'Functie-eisen',
  benefits: 'Wat wij bieden',
  conditions: 'Arbeidsvoorwaarden',
};

/**
 * @param {Object} params
 * @param {Object} params.sections  - { job_title: { content, status }, ... }
 * @param {string} [params.title]   - Document title
 * @returns {boolean}
 */
export function generateVacancyPDFFromSections({ sections, title }) {
  const filled = SECTION_ORDER
    .filter(k => k !== 'job_title' && sections[k]?.content?.trim())
    .map(k => ({ key: k, label: SECTION_LABELS[k], content: sections[k].content.trim() }));

  if (filled.length === 0) return false;

  const pdf = new jsPDF({ unit: 'mm', format: 'a4', compress: true });
  const W = 210;
  const H = 297;
  const ML = 22;
  const MR = 22;
  const CW = W - ML - MR;

  const jobTitle = sections.job_title?.content?.trim() || title || '';
  const dateStr = new Date().toLocaleDateString('nl-NL', {
    day: 'numeric', month: 'long', year: 'numeric',
  });

  // Paper background fills the entire page (very light, near-white on print).
  drawPaperBg(pdf, W, H);

  // Header band, slim teal/ink line with brand on top of every page
  drawHeaderBand(pdf, W, ML);

  // ══════════════════════════════════════════════════════════
  // TITLE AREA
  // ══════════════════════════════════════════════════════════
  let y = 40;

  // Eyebrow above title, "- VACATURE · {date}"
  pdf.setFont('courier', 'bold');
  pdf.setFontSize(8);
  pdf.setTextColor(...C.teal);
  pdf.text(`- VACATURE  ·  ${dateStr.toUpperCase()}`, ML, y);
  y += 9;

  // Big title
  if (jobTitle) {
    pdf.setFont('helvetica', 'bold');
    const fontSize = jobTitle.length > 40 ? 24 : jobTitle.length > 25 ? 30 : 36;
    pdf.setFontSize(fontSize);
    pdf.setTextColor(...C.ink);
    const titleLines = pdf.splitTextToSize(jobTitle, CW);
    titleLines.slice(0, 2).forEach((line, i) => {
      pdf.text(line, ML, y + (fontSize * 0.42));
      y += fontSize * 0.5;
    });
    y += 6;
  }

  // Heavier divider line below title (3px stroke for emphasis)
  pdf.setDrawColor(...C.ink);
  pdf.setLineWidth(0.4);
  pdf.line(ML, y, ML + CW, y);
  y += 16;

  // ══════════════════════════════════════════════════════════
  // BODY. Sections with numbered mono eyebrows
  // ══════════════════════════════════════════════════════════
  filled.forEach((section, idx) => {
    const num = String(idx + 1).padStart(2, '0');

    // Page break check before section header
    if (y > 245) {
      pdf.addPage();
      drawPaperBg(pdf, W, H);
      drawHeaderBand(pdf, W, ML);
      y = 40;
    }

    // Numbered mono eyebrow, "01. OVER HET BEDRIJF"
    pdf.setFont('courier', 'bold');
    pdf.setFontSize(8);
    pdf.setTextColor(...C.teal);
    pdf.text(`${num} ,  ${section.label.toUpperCase()}`, ML, y);
    y += 8;

    // Section body
    y = drawContent(pdf, section.content, y, ML, CW, W, H);

    // Spacer + hairline between sections (skip after last)
    if (idx < filled.length - 1) {
      y += 8;
      pdf.setDrawColor(...C.line);
      pdf.setLineWidth(0.25);
      pdf.line(ML, y, ML + CW, y);
      y += 10;
    } else {
      y += 6;
    }
  });

  // ══════════════════════════════════════════════════════════
  // FOOTER on every page
  // ══════════════════════════════════════════════════════════
  const pages = pdf.internal.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    pdf.setPage(i);
    drawFooter(pdf, W, ML, i, pages);
  }

  const fn = jobTitle
    ? `Vacature - ${jobTitle.replace(/[^a-zA-Z0-9\sÀ-ɏ-]/g, '').trim()}.pdf`
    : 'vacaturetekst.pdf';
  pdf.save(fn);
  return true;
}


// ═══════════════════════════════════════════════════════════════
// DRAWING HELPERS
// ═══════════════════════════════════════════════════════════════

function drawPaperBg(pdf, W, H) {
  pdf.setFillColor(...C.paper);
  pdf.rect(0, 0, W, H, 'F');
}

function drawHeaderBand(pdf, W, ML) {
  // Quiet paper-toned header zone, no dark band, matching Mano's landing nav
  // which uses only ink-on-paper contrast. The brand block sits in the top-left
  // (like a newspaper masthead), date in the top-right.

  // Left: stacked brand, "Vakature" bold ink + "door Humanaize" muted tagline
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(13);
  pdf.setTextColor(...C.ink);
  pdf.text('Humanaize Matcher', ML, 14);

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(7.5);
  pdf.setTextColor(...C.muted);
  pdf.text('door HumanAIze', ML, 19);

  // Right: doc-type eyebrow, mono uppercase teal
  pdf.setFont('courier', 'bold');
  pdf.setFontSize(7);
  pdf.setTextColor(...C.teal);
  pdf.text('VACATURETEKST', W - ML, 14, { align: 'right' });

  // Subtle hairline separator below header zone
  pdf.setDrawColor(...C.line);
  pdf.setLineWidth(0.2);
  pdf.line(ML, 24, W - ML, 24);
}

function drawContent(pdf, content, startY, ML, CW, W, H) {
  let y = startY;
  const lineH = 5;
  const PAGE_BREAK_Y = H - 30; // leave room for footer

  pdf.setTextColor(...C.ink2);
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(10.5);

  const paragraphs = content.split('\n');

  for (const raw of paragraphs) {
    const trimmed = raw.trim();
    if (!trimmed) { y += 2; continue; }

    if (y > PAGE_BREAK_Y) {
      pdf.addPage();
      drawPaperBg(pdf, W, H);
      drawHeaderBand(pdf, W, ML);
      y = 40;
      pdf.setTextColor(...C.ink2);
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10.5);
    }

    const isBullet = /^[\u2022\u2013\u2014\-*]\s/.test(trimmed);

    if (isBullet) {
      const text = trimmed.replace(/^[\u2022\u2013\u2014\-*]\s*/, '');
      // Small teal square bullet (matches landing's editorial style)
      pdf.setFillColor(...C.teal);
      pdf.rect(ML + 1, y - 0.5, 1.6, 1.6, 'F');
      pdf.setTextColor(...C.ink2);
      const lines = pdf.splitTextToSize(text, CW - 8);
      pdf.text(lines, ML + 7, y + 2);
      y += lines.length * lineH + 1;
    } else {
      pdf.setTextColor(...C.ink2);
      const lines = pdf.splitTextToSize(trimmed, CW);
      pdf.text(lines, ML, y + 2);
      y += lines.length * lineH + 2;
    }
  }

  return y + 1;
}

function drawFooter(pdf, W, ML, pageNum, totalPages) {
  const footerTop = 282;
  const footerY = 288;

  // Hairline above
  pdf.setDrawColor(...C.line);
  pdf.setLineWidth(0.25);
  pdf.line(ML, footerTop, W - ML, footerTop);

  // Left: brand eyebrow + URL
  pdf.setFont('courier', 'bold');
  pdf.setFontSize(7);
  pdf.setTextColor(...C.teal);
  pdf.text('HUMANAIZE MATCHER', ML, footerY);

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(7);
  pdf.setTextColor(...C.muted);
  pdf.text('humanaize.nl', ML, footerY + 4);

  // Right: page number (mono, feels editorial)
  pdf.setFont('courier', 'bold');
  pdf.setFontSize(7);
  pdf.setTextColor(...C.muted);
  pdf.text(`${String(pageNum).padStart(2, '0')} / ${String(totalPages).padStart(2, '0')}`, W - ML, footerY, { align: 'right' });
}
